import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../../Context/Provider";

const fieldClass = "bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white";

function LoginPage() {
  const auth = useContext(AppContext);
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [expirySeconds, setExpirySeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const otpMode = mode === "loginOtp" || mode === "resetOtp";

  useEffect(() => {
    if (!otpMode || (!resendSeconds && !expirySeconds)) return undefined;
    const timer = window.setInterval(() => {
      setResendSeconds((value) => Math.max(0, value - 1));
      setExpirySeconds((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpMode, resendSeconds, expirySeconds]);

  const startTimers = (result) => {
    setResendSeconds(result.resendCooldownSeconds || 60);
    setExpirySeconds(result.otpExpiresInSeconds || 180);
  };

  const submit = async () => {
    setLoading(true);
    let result;
    if (mode === "login") {
      result = await auth.LoginUser({ username, password });
      if (result?.otpRequired) {
        setUserId(result.userId);
        setEmail(result.email || "");
        setChallengeToken(result.otpChallengeToken);
        startTimers(result);
        setMode("loginOtp");
      }
    } else if (mode === "forgot") {
      result = await auth.RequestPasswordReset(identifier.trim());
      if (result?.success) {
        setChallengeToken(result.otpChallengeToken);
        startTimers(result);
        setMode("resetOtp");
      }
    } else if (mode === "loginOtp") {
      await auth.VerifyOTP({ userId, OTP: otp });
    } else if (mode === "resetOtp") {
      result = await auth.VerifyPasswordResetOTP(challengeToken, otp);
      if (result?.success) {
        setResetToken(result.resetAuthorizationToken);
        setPassword("");
        setMode("resetPassword");
      }
    } else {
      result = await auth.ResetForgottenPassword({
        resetAuthorizationToken: resetToken,
        password,
        confirmPassword,
      });
      if (result?.success) {
        setMode("login");
        setPassword("");
        setConfirmPassword("");
      }
    }
    setLoading(false);
  };

  const resend = async () => {
    if (resendSeconds || loading) return;
    setLoading(true);
    const result = mode === "loginOtp"
      ? await auth.ResendLoginOTP(challengeToken)
      : await auth.ResendPasswordResetOTP(challengeToken);
    setLoading(false);
    if (result?.success) {
      setOtp("");
      startTimers(result);
    } else if (result?.retryAfterSeconds) {
      setResendSeconds(result.retryAfterSeconds);
    }
  };

  const titles = { login: "Login to your account", loginOtp: "Enter verification code", forgot: "Forgot password", resetOtp: "Verify reset code", resetPassword: "Choose a new password" };
  const buttonText = { login: "Login", forgot: "Send reset code", loginOtp: "Verify code", resetOtp: "Verify code", resetPassword: "Reset password" };

  return (
    <section className="bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen">
        <div className="w-full bg-white rounded-lg shadow sm:max-w-md dark:bg-gray-800">
          <div className="p-6 space-y-4 sm:p-8">
            <h1 className="text-xl font-bold text-gray-900 md:text-2xl dark:text-white">{titles[mode]}</h1>
            {mode === "login" && <>
              <label className="block text-sm dark:text-white">Username<input className={fieldClass} value={username} onChange={(e) => setUsername(e.target.value)} /></label>
              <label className="block text-sm dark:text-white">
                Password
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} className={`${fieldClass} pr-12`} value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button type="button" className="absolute inset-y-0 right-3 flex items-center text-xs text-gray-600 dark:text-gray-300" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </label>
              <button type="button" className="text-sm text-green-600" onClick={() => setMode("forgot")}>Forgot password?</button>
            </>}
            {mode === "forgot" && <label className="block text-sm dark:text-white">Username or email<input className={fieldClass} value={identifier} onChange={(e) => setIdentifier(e.target.value)} /></label>}
            {otpMode && <>
              {email && mode === "loginOtp" ? <p className="text-sm text-gray-600">Code sent to {email}</p> : null}
              <label className="block text-sm dark:text-white">6-digit code<input className={fieldClass} inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} /></label>
              <div className="flex justify-between text-sm">
                <span>{expirySeconds ? `Expires in ${Math.floor(expirySeconds / 60)}:${String(expirySeconds % 60).padStart(2, "0")}` : "Code expired"}</span>
                <button type="button" onClick={resend} disabled={resendSeconds > 0 || loading} className="text-green-600 disabled:text-gray-400">{resendSeconds ? `Resend in ${resendSeconds}s` : "Resend code"}</button>
              </div>
            </>}
            {mode === "resetPassword" && <>
              <label className="block text-sm dark:text-white">
                New password
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} className={`${fieldClass} pr-12`} value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button type="button" className="absolute inset-y-0 right-3 flex items-center text-xs text-gray-600 dark:text-gray-300" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </label>
              <label className="block text-sm dark:text-white">
                Confirm password
                <div className="relative">
                  <input type={showConfirmPassword ? "text" : "password"} className={`${fieldClass} pr-12`} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                  <button type="button" className="absolute inset-y-0 right-3 flex items-center text-xs text-gray-600 dark:text-gray-300" onClick={() => setShowConfirmPassword((value) => !value)} aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}>
                    {showConfirmPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </label>
            </>}
            <button type="button" disabled={loading || (otpMode && (otp.length !== 6 || !expirySeconds))} onClick={submit} className="w-full text-white bg-custom-green rounded-lg px-5 py-2.5 disabled:opacity-60">{loading ? "Please wait..." : buttonText[mode]}</button>
            {mode !== "login" && <button type="button" className="w-full text-sm text-gray-600" onClick={() => setMode("login")}>Back to login</button>}
          </div>
        </div>
      </div>
    </section>
  );
}

export default LoginPage;
